// Pure helpers of the Atelier (no DOM): session state labels, which actions are possible, message
// merging and grouping, live event application (SSE 'atelier' kinds text/tool/state/diff), test
// output tails and message validation. Tested in test/ui-atelier.test.js.
import { t } from '../../i18n/fr.js';
import './strings.js';

/** Longest message the server accepts (server/atelier/service.js MAX_MESSAGE_CHARS). */
export const MAX_MESSAGE_CHARS = 20_000;
/** Session states (SPEC §18). */
export const SESSION_STATES = ['running', 'ready', 'testing', 'applied', 'discarded', 'failed'];
/** Final states: nothing more can happen to the session. */
export const FINAL_STATES = new Set(['applied', 'discarded']);
/** localStorage key remembering that the intro banner was dismissed. */
export const INTRO_KEY = 'mo.atelier.intro.v1';
/** Messages scanned for duplicates when a live message arrives. */
const DEDUPE_WINDOW = 60;

function str(v) {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

/**
 * Badge of a session: label, tone and whether something is in progress.
 * @param {{state?: string, busy?: string|null}} s
 * @returns {{key: string, label: string, tone: string, live: boolean}}
 */
export function stateInfo(s = {}) {
  const busy = s?.busy || null;
  if (busy === 'apply') return { key: 'applying', label: t('atl.state.applying'), tone: 'running', live: true };
  if (busy === 'discard') return { key: 'discarding', label: t('atl.state.discarding'), tone: 'running', live: true };
  switch (s?.state) {
    case 'running': return { key: 'running', label: t('atl.state.running'), tone: 'running', live: true };
    case 'testing': return { key: 'testing', label: t('atl.state.testing'), tone: 'running', live: true };
    case 'ready': return { key: 'ready', label: t('atl.state.ready'), tone: 'info', live: false };
    case 'applied': return { key: 'applied', label: t('atl.state.applied'), tone: 'success', live: false };
    case 'discarded': return { key: 'discarded', label: t('atl.state.discarded'), tone: 'muted', live: false };
    case 'failed': return { key: 'failed', label: t('atl.state.failed'), tone: 'danger', live: false };
    default: return { key: 'unknown', label: t('atl.state.unknown'), tone: 'neutral', live: false };
  }
}

/** True when the session can no longer change. */
export function isFinal(s) {
  return FINAL_STATES.has(s?.state);
}

/**
 * Why the composer cannot send right now (a French sentence), or '' when it can.
 * @param {object|null} session the current session, or null for a new one
 * @param {{status?: object|null}} [env] GET /api/atelier/status (enabled, repo.ok, running)
 */
export function sendBlocker(session, { status = null } = {}) {
  if (status && status.enabled === false) return t('atl.chat.blocked.disabled');
  if (!session) {
    if (status && status.repo && status.repo.ok === false) return t('atl.chat.blocked.repo');
    if (status?.running) return t('atl.chat.blocked.other');
    return '';
  }
  if (session.state === 'applied') return t('atl.chat.blocked.applied');
  if (session.state === 'discarded') return t('atl.chat.blocked.discarded');
  if (session.state === 'running' || session.busy === 'claude') return t('atl.chat.blocked.running');
  if (session.state === 'testing' || session.busy === 'test') return t('atl.chat.blocked.testing');
  if (session.busy) return t('atl.chat.blocked.busy');
  if (status?.running && status.running !== session.id) return t('atl.chat.blocked.other');
  return '';
}

/**
 * What the changes panel may offer for a session (mirrors the server's checks; the server still
 * refuses the rest).
 * @param {object|null} session
 * @param {{mock?: boolean, hasChanges?: boolean|null}} [env] hasChanges: null when unknown
 * @returns {{test: boolean, apply: boolean, discard: boolean, needsTests: boolean, why: {test?: string, apply?: string, discard?: string}}}
 */
export function sessionActions(session, { mock = false, hasChanges = null } = {}) {
  const why = {};
  if (!session) return { test: false, apply: false, discard: false, needsTests: false, why };
  const final = isFinal(session);
  const busy = session.busy || (session.state === 'running' ? 'claude' : session.state === 'testing' ? 'test' : null);
  const needsTests = session.tests?.ok !== true;

  let test = !final && !busy && ['ready', 'failed'].includes(session.state);
  if (final) why.test = t('atl.action.why.final');
  else if (busy) why.test = t('atl.action.why.busy');
  if (hasChanges === false && test) { test = false; why.test = t('atl.action.why.empty'); }

  let apply = !final && !busy && session.state === 'ready';
  if (final) why.apply = t('atl.action.why.final');
  else if (busy) why.apply = t('atl.action.why.busy');
  else if (session.state === 'failed') why.apply = t('atl.action.why.failed');
  if (apply && mock) { apply = false; why.apply = t('atl.action.why.mock'); }
  if (apply && hasChanges === false) { apply = false; why.apply = t('atl.action.why.empty'); }
  if (apply && needsTests) why.apply = t('atl.action.why.tests');

  const discard = !final && busy !== 'apply' && busy !== 'discard';
  if (!discard) why.discard = final ? t('atl.action.why.final') : t('atl.action.why.busy');
  return { test, apply, discard, needsTests, why };
}

/** Identity of a message (the server sends the same role/at/text in GETs and live events). */
export function messageKey(m) {
  return `${str(m?.role)}\u0001${str(m?.at)}\u0001${str(m?.text)}`;
}

/**
 * Appends a live message unless the session already holds it. Returns the same session object
 * when nothing changed, else a new one.
 * @param {object} session
 * @param {{role: string, text: string, at: string}} msg
 */
export function appendMessage(session, msg) {
  if (!session || !msg || typeof msg.text !== 'string') return session;
  const role = ['user', 'claude', 'step'].includes(msg.role) ? msg.role : 'step';
  const clean = { role, text: msg.text, at: str(msg.at) };
  const list = Array.isArray(session.messages) ? session.messages : [];
  const key = messageKey(clean);
  for (let i = list.length - 1; i >= Math.max(0, list.length - DEDUPE_WINDOW); i--) {
    if (messageKey(list[i]) === key) return session;
  }
  return { ...session, messages: [...list, clean], updatedAt: clean.at || session.updatedAt };
}

/**
 * Applies one SSE 'atelier' event to the open (full) session. Returns the same object when the
 * event is for another session or changes nothing.
 * @param {object|null} session
 * @param {{sessionId: string, kind: string, role?: string, text?: string, at?: string,
 *   state?: string, session?: object, diffStat?: object}} ev
 */
export function applyEvent(session, ev) {
  if (!session || !ev || ev.sessionId !== session.id) return session;
  switch (ev.kind) {
    case 'text':
    case 'tool':
      return appendMessage(session, { role: ev.kind === 'tool' ? 'step' : ev.role, text: str(ev.text), at: ev.at });
    case 'state': {
      const summary = ev.session && typeof ev.session === 'object' ? ev.session : null;
      if (!summary || summary.id !== session.id) return { ...session, state: ev.state || session.state };
      // The summary is the whole record minus the transcript (server/atelier/sessions.js
      // sessionSummary, with the test output): it is authoritative, so a field it lacks (an
      // error or a test result cleared by a new run) is gone.
      const { messages: _m, messageCount: _c, lastMessage: _l, ...rest } = summary;
      const next = { ...rest, messages: session.messages, state: ev.state || rest.state || session.state, busy: rest.busy ?? null };
      // Defensive: keep the output we have when a summary of the same test run omits it.
      if (next.tests && session.tests && next.tests.at === session.tests.at && next.tests.output === undefined) {
        next.tests = { ...next.tests, output: session.tests.output };
      }
      return next;
    }
    case 'diff':
      return ev.diffStat && typeof ev.diffStat === 'object' ? { ...session, diffStat: ev.diffStat } : session;
    default:
      return session;
  }
}

/**
 * Applies one SSE event to the sessions list (summaries, newest first). Unknown sessions are
 * added when the event carries a summary (kind 'state').
 * @param {object[]|null} list
 * @param {object} ev
 * @returns {object[]|null}
 */
export function applyEventToList(list, ev) {
  if (!Array.isArray(list) || !ev || !ev.sessionId) return list;
  const idx = list.findIndex((s) => s.id === ev.sessionId);
  if (idx < 0) {
    if (ev.kind === 'state' && ev.session && ev.session.id === ev.sessionId) return sortSessions([ev.session, ...list]);
    return list;
  }
  const cur = list[idx];
  let next = cur;
  if (ev.kind === 'state' && ev.session) {
    next = { ...cur, ...ev.session, state: ev.state || ev.session.state || cur.state };
  } else if (ev.kind === 'text' || ev.kind === 'tool') {
    next = {
      ...cur,
      updatedAt: ev.at || cur.updatedAt,
      messageCount: (cur.messageCount || 0) + 1,
      lastMessage: { role: ev.kind === 'tool' ? 'step' : ev.role, text: str(ev.text).slice(0, 200), at: ev.at },
    };
  } else if (ev.kind === 'diff' && ev.diffStat) {
    next = { ...cur, diffStat: ev.diffStat };
  }
  if (next === cur) return list;
  const out = [...list];
  out[idx] = next;
  return out;
}

/** Sessions sorted newest first (createdAt). */
export function sortSessions(list) {
  return [...(Array.isArray(list) ? list : [])].filter((s) => s && s.id)
    .sort((a, b) => str(b.createdAt).localeCompare(str(a.createdAt)) || str(b.id).localeCompare(str(a.id)));
}

/** Replaces (or adds) a session summary built from a full session. */
export function upsertSession(list, session) {
  if (!session?.id) return list;
  const { messages, ...rest } = session;
  const summary = { ...rest };
  if (Array.isArray(messages)) {
    const last = messages[messages.length - 1];
    summary.messageCount = messages.length;
    summary.lastMessage = last ? { role: last.role, text: str(last.text).slice(0, 200), at: last.at } : null;
  }
  const base = Array.isArray(list) ? list : [];
  const idx = base.findIndex((s) => s.id === session.id);
  if (idx < 0) return sortSessions([summary, ...base]);
  const out = [...base];
  out[idx] = { ...base[idx], ...summary };
  return out;
}

/**
 * Groups a transcript for display: user and Claude messages one by one, consecutive steps
 * together.
 * @param {Array<{role: string, text: string, at: string}>} messages
 * @returns {Array<{type: 'user'|'claude', message: object} | {type: 'steps', steps: object[]}>}
 */
export function groupMessages(messages) {
  const out = [];
  for (const m of Array.isArray(messages) ? messages : []) {
    if (!m || typeof m !== 'object') continue;
    if (m.role === 'step') {
      const last = out[out.length - 1];
      if (last && last.type === 'steps') last.steps.push(m);
      else out.push({ type: 'steps', steps: [m] });
    } else {
      out.push({ type: m.role === 'user' ? 'user' : 'claude', message: m });
    }
  }
  return out;
}

/**
 * Display form of a step line (from server/atelier/claude.js describeTool and the service):
 * "✎ web/ui/topbar.js" (edit), "$ npm test" → "▶ npm test" (command), "Lecture : …" (read),
 * "Recherche …" (search), "✓ …" (ok), "✗ …" / "Échec …" (fail), anything else (info).
 * @param {string} text
 * @returns {{kind: 'edit'|'command'|'read'|'search'|'ok'|'fail'|'info', text: string}}
 */
export function stepView(text) {
  const s = str(text).trim();
  if (s.startsWith('✎')) return { kind: 'edit', text: s };
  if (s.startsWith('$ ')) return { kind: 'command', text: `▶ ${s.slice(2)}` };
  if (s.startsWith('▶')) return { kind: 'command', text: s };
  if (/^Lecture\b/.test(s)) return { kind: 'read', text: s };
  if (/^Recherche\b/.test(s)) return { kind: 'search', text: s };
  if (s.startsWith('✓')) return { kind: 'ok', text: s };
  if (s.startsWith('✗') || /^Échec\b/.test(s)) return { kind: 'fail', text: s };
  return { kind: 'info', text: s };
}

/**
 * The last `lines` lines of a test output.
 * @param {string} output
 * @param {number} [lines]
 * @returns {{text: string, shown: number, total: number, truncated: boolean}}
 */
export function testTail(output, lines = 40) {
  const all = str(output).replace(/\r\n/g, '\n').replace(/\n+$/, '');
  if (!all) return { text: '', shown: 0, total: 0, truncated: false };
  const list = all.split('\n');
  const n = Math.max(1, lines);
  const tail = list.slice(-n);
  return { text: tail.join('\n'), shown: tail.length, total: list.length, truncated: list.length > n };
}

/**
 * Validates a message before sending (same rules as the server).
 * @param {string} text
 * @returns {{ok: boolean, value: string, error: string}}
 */
export function validateMessage(text) {
  // eslint-disable-next-line no-control-regex
  const value = str(text).replace(/\r\n/g, '\n').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '').trim();
  if (!value) return { ok: false, value, error: t('atl.chat.empty') };
  if (value.length > MAX_MESSAGE_CHARS) return { ok: false, value, error: t('atl.chat.tooLong', { max: MAX_MESSAGE_CHARS.toLocaleString('fr-FR') }) };
  return { ok: true, value, error: '' };
}

/**
 * "3 fichiers · +120 −8" for a diffStat ({files, insertions, deletions}); '' when empty/unknown.
 * @param {{files?: number, insertions?: number, deletions?: number}|null} stat
 */
export function formatStat(stat) {
  if (!stat || typeof stat !== 'object' || !Number(stat.files)) return '';
  return t('atl.changes.stat', {
    files: t('atl.changes.files', { n: Number(stat.files) }),
    ins: Number(stat.insertions) || 0,
    del: Number(stat.deletions) || 0,
  });
}

/** Normalises GET /api/atelier/sessions (an array, or `{sessions}`). */
export function normalizeSessions(res) {
  const list = Array.isArray(res) ? res : Array.isArray(res?.sessions) ? res.sessions : [];
  return sortSessions(list.filter((s) => s && typeof s === 'object' && typeof s.id === 'string'));
}

/**
 * True when the Atelier can start a new session (status known and ready). Unknown status
 * (endpoint missing) lets the server decide.
 */
export function canStart(status) {
  if (!status) return true;
  return status.enabled !== false && status.repo?.ok !== false;
}
